export type ProjectHealthLabel = "On Track" | "At Risk" | "Inactive";

export interface ProjectHealth {
  label: ProjectHealthLabel;
  bg: string;
  color: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function parseDateLocal(dateString?: string): Date | null {
  if (!dateString) return null;
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const [, y, m, d] = match.map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Progress of a project as a percentage (0-100) based on its start/end dates
 * relative to today. Clamped so it never exceeds 0..100.
 */
export function getProjectProgress(startDate?: string, endDate?: string): number {
  const start = parseDateLocal(startDate);
  const end = parseDateLocal(endDate);
  if (!start || !end) return 0;

  const now = new Date();
  const total = end.getTime() - start.getTime();
  if (total <= 0) return end.getTime() <= now.getTime() ? 100 : 0;

  const elapsed = now.getTime() - start.getTime();
  const pct = Math.round((elapsed / total) * 100);
  return Math.min(100, Math.max(0, pct));
}

/**
 * Delivery health of a project using semantic color tokens.
 * - Inactive projects are neutral/gray.
 * - Active projects with <= 7 days left (or past the due date) are At Risk.
 * - Everything else is On Track.
 */
export function getProjectHealth(status?: string, endDate?: string): ProjectHealth {
  const key = String(status || "").toLowerCase();
  const isActive = key === "active";

  if (!isActive) {
    return {
      label: "Inactive",
      bg: "var(--color-bg-muted)",
      color: "var(--color-text-secondary)",
    };
  }

  const end = parseDateLocal(endDate);
  if (end) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const daysLeft = Math.round((end.getTime() - today.getTime()) / DAY_MS);
    if (daysLeft <= 7) {
      return {
        label: "At Risk",
        bg: "var(--color-error-light)",
        color: "var(--color-error-dark)",
      };
    }
  }

  return {
    label: "On Track",
    bg: "var(--color-success-light)",
    color: "var(--color-success-dark)",
  };
}
